import { Link } from "wouter";
import { Shield, ExternalLink } from "lucide-react";
import { EDMUNDO_SITE } from "@shared/../shared/constants";
import NewsletterForm from "@/components/NewsletterForm";
import MentoringDialog from "@/components/MentoringDialog";
import { useLocale } from "@/contexts/LocaleContext";

export default function Footer() {
  const { t, locale } = useLocale();
  const aboutPath = locale === "en" ? "/about-the-book" : "/sobre-el-libro";
  const edmundoPath = locale === "en" ? "/about-edmundo" : "/edmundo-trevino";

  return (
    <footer className="border-t border-border/50 bg-navy-dark">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-6 w-6 text-gold" />
              <span className="font-serif text-lg font-bold text-gold">{t("footer.brand")}</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("footer.brandDesc")}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-serif text-sm font-semibold text-gold mb-4 uppercase tracking-wider">{t("footer.navigation")}</h4>
            <ul className="space-y-2">
              <li><Link href="/" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t("nav.home")}</Link></li>
              <li><Link href="/blog" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t("nav.blog")}</Link></li>
              <li><Link href="/ideas" className="text-sm text-muted-foreground hover:text-gold transition-colors">{t("nav.ideas")}</Link></li>
              <li><Link href={aboutPath} className="text-sm text-muted-foreground hover:text-gold transition-colors">{t("nav.about")}</Link></li>
            </ul>
          </div>

          {/* Edmundo Treviño */}
          <div>
            <h4 className="font-serif text-sm font-semibold text-gold mb-4 uppercase tracking-wider">{t("footer.edmundo")}</h4>
            <ul className="space-y-2">
              <li>
                <Link href={edmundoPath} className="text-sm text-muted-foreground hover:text-gold transition-colors">
                  {locale === "en" ? "About Edmundo" : "Sobre Edmundo"}
                </Link>
              </li>
              <li>
                <a href={EDMUNDO_SITE} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1">
                  {t("footer.personalSite")} <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li>
                <a href={`${aboutPath}#opciones`} className="text-sm text-muted-foreground hover:text-gold transition-colors">
                  {t("footer.buyBook")}
                </a>
              </li>
              <li>
                <MentoringDialog>
                  <button type="button" className="text-sm text-muted-foreground hover:text-gold transition-colors text-left">
                    {t("footer.bookConsulting")}
                  </button>
                </MentoringDialog>
              </li>
            </ul>
          </div>

          {/* Newsletter + Social */}
          <div className="space-y-6">
            <NewsletterForm variant="compact" />
            <div className="flex gap-3">
              <a href="https://www.facebook.com/edmundotrevinomx" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-gold transition-colors" aria-label="Facebook">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.youtube.com/@comprandoamerica" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-gold transition-colors" aria-label="YouTube">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.linkedin.com/in/edmundotrevino/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-gold transition-colors" aria-label="LinkedIn">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-border/50 mt-8 pt-8 text-center">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
}
